import { useEffect, useRef, useState } from 'react';
import { COLORS, INPUT_STYLE } from '../../constants/theme';

interface CreateUniverseWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = '1' | '2' | 'success';

const STANDARD_AGE_LENGTH_NOTE =
  'Standard age length is 12 human weeks — about 12 Utopian years (2,016 Utopian days). Adjustable once created.';

/**
 * Two-step Create Universe wizard (SF-103). Mock-data only — nothing actually persists, so the
 * success panel is phrased as simulated (Decision 6), not past-tense-real.
 */
export function CreateUniverseWizard({ isOpen, onClose }: CreateUniverseWizardProps) {
  const [step, setStep] = useState<Step>('1');
  const [universeName, setUniverseName] = useState('');
  const [ageName, setAgeName] = useState('');
  const [worldName, setWorldName] = useState('');
  const [showNameError, setShowNameError] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<Element | null>(null);

  const isNameTaken = universeName.trim().toLowerCase() === 'prime';

  useEffect(() => {
    if (!isOpen) return;

    triggerRef.current = document.activeElement;
    setStep('1');
    setUniverseName('');
    setAgeName('');
    setWorldName('');
    setShowNameError(false);
    nameInputRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'input, button, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleNext = () => {
    if (!universeName.trim() || isNameTaken) {
      setShowNameError(true);
      nameInputRef.current?.focus();
      return;
    }
    setShowNameError(false);
    setStep('2');
  };

  const finalUniverseName = universeName.trim() || 'New Universe';
  const finalAgeName = ageName.trim() || 'Age 1';
  const finalWorldName = worldName.trim() || 'Div 1';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-universe-title"
        className="rounded-lg shadow-xl max-w-lg w-full mx-4"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          <span id="create-universe-title" className="text-base font-semibold" style={{ color: COLORS.text.primary }}>
            {step === 'success' ? `${finalUniverseName} would be created` : 'New Universe'}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ color: COLORS.text.tertiary, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
          >
            ×
          </button>
        </div>

        {step !== 'success' && (
          <div className="flex items-center gap-2 px-6 pt-4 text-xs">
            <span style={{ color: step === '1' ? COLORS.link : COLORS.text.tertiary, fontWeight: step === '1' ? 700 : 400 }}>
              1. Universe
            </span>
            <span style={{ color: COLORS.text.tertiary }}>—</span>
            <span style={{ color: step === '2' ? COLORS.link : COLORS.text.tertiary, fontWeight: step === '2' ? 700 : 400 }}>
              2. Age 1 &amp; Div 1
            </span>
          </div>
        )}

        <div className="p-6">
          {step === '1' && (
            <>
              <label htmlFor="universe-name" className="block mb-1.5 text-sm" style={{ color: COLORS.text.tertiary }}>
                Universe name
              </label>
              <input
                ref={nameInputRef}
                id="universe-name"
                type="text"
                placeholder="e.g. Prime"
                autoComplete="off"
                value={universeName}
                onChange={(e) => { setUniverseName(e.target.value); setShowNameError(false); }}
                className="w-full px-3 py-2 rounded focus:outline-none"
                style={INPUT_STYLE}
              />
              <p className="mt-1.5 text-xs" style={{ color: COLORS.text.tertiary }}>
                A universe is a fully independent ruleset and kingdom pool. Must be unique.
              </p>
              {showNameError && (
                <p className="mt-1 text-xs" style={{ color: COLORS.negative }}>
                  {isNameTaken
                    ? 'A universe named "Prime" already exists — try another name.'
                    : 'Universe name is required.'}
                </p>
              )}

              <div className="mt-4 px-4 py-3 rounded text-sm" style={{ backgroundColor: COLORS.cardAlt, border: `1px solid ${COLORS.border}` }}>
                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: COLORS.text.tertiary }}>
                  Will create
                </div>
                <div style={{ color: universeName.trim() && !isNameTaken ? COLORS.text.primary : COLORS.text.tertiary }}>
                  {universeName.trim() ? (isNameTaken ? 'Choose a different name' : universeName.trim()) : 'Not named yet'}
                </div>
              </div>
            </>
          )}

          {step === '2' && (
            <>
              <label htmlFor="age-name" className="block mb-1.5 text-sm" style={{ color: COLORS.text.tertiary }}>
                Age 1 name
              </label>
              <input
                id="age-name"
                type="text"
                placeholder="e.g. The Kindling"
                autoComplete="off"
                value={ageName}
                onChange={(e) => setAgeName(e.target.value)}
                className="w-full px-3 py-2 rounded focus:outline-none mb-4"
                style={INPUT_STYLE}
              />

              <label htmlFor="world-name" className="block mb-1.5 text-sm" style={{ color: COLORS.text.tertiary }}>
                First world name (becomes Div 1)
              </label>
              <input
                id="world-name"
                type="text"
                placeholder="e.g. Emberreach"
                autoComplete="off"
                value={worldName}
                onChange={(e) => setWorldName(e.target.value)}
                className="w-full px-3 py-2 rounded focus:outline-none"
                style={INPUT_STYLE}
              />

              <div
                className="mt-4 px-4 py-3 rounded text-sm flex gap-2"
                style={{ backgroundColor: COLORS.cardAlt, border: `1px solid ${COLORS.border}`, color: COLORS.text.tertiary }}
              >
                <span style={{ color: COLORS.text.primary, fontFamily: 'monospace' }}>12 wk</span>
                <span>{STANDARD_AGE_LENGTH_NOTE}</span>
              </div>

              <div className="mt-4 px-4 py-3 rounded text-sm" style={{ backgroundColor: COLORS.cardAlt, border: `1px solid ${COLORS.border}` }}>
                <div className="text-xs uppercase tracking-wide mb-1" style={{ color: COLORS.text.tertiary }}>
                  Will create
                </div>
                <div style={{ color: COLORS.text.primary }}>{universeName.trim()}</div>
                <div style={{ color: ageName.trim() || worldName.trim() ? COLORS.text.secondary : COLORS.text.tertiary }}>
                  {ageName.trim() || worldName.trim()
                    ? `Age 1${ageName.trim() ? ` — ${ageName.trim()}` : ''} · Pending${worldName.trim() ? ` · Div 1: ${worldName.trim()}` : ''}`
                    : 'Age 1 not named yet'}
                </div>
              </div>
            </>
          )}

          {step === 'success' && (
            <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
              {finalAgeName} would be Pending. {finalWorldName} (Div 1) would be ready to receive its first kingdoms
              once the age starts — no data was actually created.
            </p>
          )}
        </div>

        <div className="flex gap-3 justify-end px-6 py-4" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          {step === '1' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded text-sm"
                style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 rounded text-sm font-medium"
                style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary }}
              >
                Next →
              </button>
            </>
          )}
          {step === '2' && (
            <>
              <button
                type="button"
                onClick={() => setStep('1')}
                className="px-4 py-2 rounded text-sm"
                style={{ backgroundColor: COLORS.cardAlt, color: COLORS.text.secondary, border: `1px solid ${COLORS.border}` }}
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={() => setStep('success')}
                className="px-4 py-2 rounded text-sm font-medium"
                style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary }}
              >
                Create Universe
              </button>
            </>
          )}
          {step === 'success' && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded text-sm font-medium"
              style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary }}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
